import { useMemo } from 'react';
import { useContent } from 'hooks/useContent';
import { mergeEvents } from 'helpers/mergeEvents';

/**
 * Календар як одне ціле.
 *
 * Сторінки не мають знати, що заходів два джерела: змагання приходять із e-phan
 * живцем, а CMS додає власні заходи й доповнення до змагань (ADR-0012). Тут ці
 * два розділи зводяться в один перелік і в один стан завантаження.
 *
 * Про збій одного з джерел. Якщо мовчить чуже API, календар показує власні
 * заходи; якщо мовчить наша CMS — показує змагання. Порожній екран з'являється
 * лише тоді, коли впали обидва. Часткову втрату видно через `partial`: сторінка
 * може сказати про неї, не ховаючи те, що вже є.
 */
export function useCalendar() {
  const competitions = useContent('competitions');
  const events = useContent('events');

  const data = useMemo(
    () => mergeEvents(competitions.data || [], events.data || []),
    [competitions.data, events.data]
  );

  const failed = [competitions, events].filter((section) => section.status === 'failed');
  const bothFailed = failed.length === 2;

  const retry = () => {
    if (competitions.status === 'failed') competitions.retry();
    if (events.status === 'failed') events.retry();
  };

  return {
    data,
    status: bothFailed ? 'failed' : competitions.isLoading || events.isLoading ? 'loading' : 'succeeded',
    error: bothFailed ? failed[0].error : null,
    // Одне джерело мовчить, друге відповіло: показуємо, що є, і кажемо про це.
    partial: failed.length === 1 ? (failed[0] === competitions ? 'competitions' : 'events') : null,
    isLoading: competitions.isLoading || events.isLoading,
    retry,
  };
}
